import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, Eye, KeyRound, Power, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ApiClient } from "../../../../../../../shared/index";
import { SaveDiffDialog, type PendingChange } from "../../components/save-diff-dialog";

/**
 * Credentials.
 *
 * The Client Token is masked, revealed once on request, and never rendered in a list.
 * The legacy tool showed it as a copyable column in the clients grid and as a plain
 * text field anyone could type over.
 */
export function KeysTab({ client }: { client: ApiClient }) {
  const queryClient = useQueryClient();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [pending, setPending] = useState<(PendingChange & { action: "rotate" | "deactivate" | "reactivate" }) | null>(
    null
  );

  const reveal = useMutation({
    mutationFn: () => api.b2b.revealToken(client.id),
    onSuccess: (data) => setRevealed(data.token),
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Reveal failed"),
  });

  const act = useMutation({
    mutationFn: async (action: "rotate" | "deactivate" | "reactivate") => {
      // Only a rotation returns a token, and only once.
      if (action === "rotate") {
        const result = await api.b2b.rotateToken(client.id);
        return { token: result.token };
      }
      if (action === "deactivate") {
        await api.b2b.deactivate(client.id);
        return { token: null };
      }
      await api.b2b.reactivate(client.id);
      return { token: null };
    },
    onSuccess: async (data, action) => {
      setPending(null);
      setRevealed(null);
      if (action === "rotate" && data.token) {
        setFreshToken(data.token);
      }
      await queryClient.invalidateQueries({ queryKey: ["b2b"] });
      toast.success(
        action === "rotate"
          ? "Token rotated. The old one stopped working on every runtime."
          : "Saved."
      );
    },
    onError: (error) => {
      setPending(null);
      toast.error(error instanceof ApiError ? error.message : "Action failed");
    },
  });

  async function copy(value: string) {
    await navigator.clipboard.writeText(value);
    toast.success("Copied");
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="space-y-4 rounded-xl border bg-card p-6">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-semibold">Credentials</h2>
        </div>

        <div className="space-y-1.5">
          <Label>Client key — sent as X-APIKey</Label>
          <div className="flex gap-2">
            <Input value={client.key} readOnly className="font-mono" />
            <Button variant="secondary" size="icon" onClick={() => copy(client.key)}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Client token — sent as X-APIToken</Label>
          <div className="flex gap-2">
            <Input
              value={revealed ?? client.tokenMasked}
              readOnly
              className="font-mono"
            />
            {revealed ? (
              <Button variant="secondary" size="icon" onClick={() => copy(revealed)}>
                <Copy className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={() => reveal.mutate()}
                disabled={reveal.isPending}
              >
                <Eye className="mr-2 h-4 w-4" />
                Reveal
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Revealing is recorded in the change log, like any other action on this client.
          </p>
        </div>

        {freshToken && (
          <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/40">
            <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
              New token — copy it now
            </p>
            <div className="flex gap-2">
              <Input value={freshToken} readOnly className="font-mono" />
              <Button variant="secondary" size="icon" onClick={() => copy(freshToken)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-emerald-900 dark:text-emerald-200">
              Send it to the customer through a channel you trust. It will not be shown again.
            </p>
          </div>
        )}
      </section>

      <section className="space-y-4 rounded-xl border bg-card p-6">
        <h2 className="font-semibold">Actions</h2>

        <div className="space-y-2">
          <Button
            variant="secondary"
            className="w-full justify-start"
            onClick={() =>
              setPending({
                action: "rotate",
                confirmLabel: "Rotate token",
                warning:
                  "The customer's current token stops working the moment you confirm. Their endpoint grants are carried over to the new token automatically.",
                diff: [{ path: "token", before: client.tokenMasked, after: "a new token" }],
              })
            }
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Rotate token
          </Button>

          {client.isActive ? (
            <Button
              variant="destructive"
              className="w-full justify-start"
              onClick={() =>
                setPending({
                  action: "deactivate",
                  confirmLabel: "Deactivate client",
                  warning:
                    "The client is marked inactive and its token is rotated, so every call starts failing immediately. The client key is kept, so its usage history stays under one name.",
                  diff: [
                    { path: "isActive", before: true, after: false },
                    { path: "token", before: client.tokenMasked, after: "a new token" },
                  ],
                })
              }
            >
              <Power className="mr-2 h-4 w-4" />
              Deactivate client
            </Button>
          ) : (
            <Button
              variant="secondary"
              className="w-full justify-start"
              onClick={() =>
                setPending({
                  action: "reactivate",
                  confirmLabel: "Reactivate client",
                  warning:
                    "The client becomes active again with its current token. If the customer no longer has it, rotate afterwards.",
                  diff: [{ path: "isActive", before: false, after: true }],
                })
              }
            >
              <Power className="mr-2 h-4 w-4" />
              Reactivate client
            </Button>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Every action here evicts the client&rsquo;s cached login on both External API
          runtimes — the legacy tool evicted only one, leaving a revoked token usable for up
          to 24 hours.
        </p>
      </section>

      <SaveDiffDialog
        pending={pending}
        saving={act.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => pending && act.mutate(pending.action)}
      />
    </div>
  );
}
